/*
 * TICK-140 — the shell both consoles render inside (Figma `08 — App Shell & Templates`, node `38:4`).
 *
 * One shell, two consoles. Event Admin and SuperAdmin differ in the registry they pass and the
 * context the top bar shows; they do not differ in how a sidebar collapses, where the primary
 * action goes on a phone or what a page heading looks like, and building that twice is how the two
 * consoles drifted apart in the first place.
 *
 * The drawer is the only piece of behaviour here, and it is behaviour because CSS cannot do the
 * parts that matter: Escape closes it, focus moves to the destination you are on when it opens and
 * returns to the toggle when it closes, and it closes on navigation so a phone does not stay
 * covered by the menu it just used.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Sidebar } from './Sidebar';
import type { SidebarProps } from './Sidebar';
import { TopBar } from './TopBar';
import { PageHeader } from './PageHeader';
import type { SectionTabItem } from '../ui';
import type { Destination } from '../destinations';

export type ConsoleTheme = 'dark' | 'light';

export interface AppShellProps<K extends string> {
  theme: ConsoleTheme;
  destination: Destination;
  phaseLabel?: string;

  sidebar: Omit<SidebarProps, 'activeItemRef'>;

  topBarContext: string;
  topBarDetail?: ReactNode;
  topBarActions?: ReactNode;
  onSearch?: (query: string) => void;
  searchLabel?: string;

  primaryAction?: ReactNode;
  secondaryActions?: ReactNode;
  tabs?: readonly SectionTabItem<K>[];
  activeTab?: K;
  onSelectTab?: (key: K) => void;

  /** `wide` for tables and canvases, `narrow` for a focused task. Mirrors the existing width token. */
  width?: 'narrow' | 'normal' | 'wide';
  children: ReactNode;
}

export function AppShell<K extends string>({
  theme, destination, phaseLabel, sidebar,
  topBarContext, topBarDetail, topBarActions, onSearch, searchLabel,
  primaryAction, secondaryActions, tabs, activeTab, onSelectTab,
  width = 'normal', children,
}: AppShellProps<K>) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleRef = useRef<HTMLElement | null>(null);
  const activeItem = useRef<HTMLButtonElement | null>(null);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    /* Focus goes back to what opened it rather than to the top of the document. */
    toggleRef.current?.focus?.();
  }, []);

  useEffect(() => {
    if (!drawerOpen) return;
    activeItem.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') closeDrawer(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen, closeDrawer]);

  /* Navigating closes the drawer: on a phone it covers the screen it just took you to. */
  const onNavigate = (next: Destination) => {
    sidebar.onNavigate(next);
    if (drawerOpen) closeDrawer();
  };

  return (
    <div className="console-v2" data-console-theme={theme} data-sidebar={drawerOpen ? 'open' : 'closed'}>
      {/* Straight to the content, past a menu of up to twenty items. */}
      <a className="jt-sr-only" href="#jt-main-content">ข้ามไปยังเนื้อหาหลัก</a>

      <Sidebar {...sidebar} onNavigate={onNavigate} activeItemRef={(el) => { activeItem.current = el; }} />

      {drawerOpen && (
        <button type="button" className="jt-sidebar-scrim" aria-label="ปิดเมนู" onClick={closeDrawer} />
      )}

      <div className="jt-main">
        <TopBar
          context={topBarContext}
          contextDetail={topBarDetail}
          onOpenSidebar={() => {
            toggleRef.current = document.activeElement as HTMLElement | null;
            setDrawerOpen(true);
          }}
          onSearch={onSearch}
          searchLabel={searchLabel}
          actions={topBarActions}
        />

        <PageHeader
          destination={destination}
          phaseLabel={phaseLabel}
          primaryAction={primaryAction}
          secondaryActions={secondaryActions}
          tabs={tabs}
          activeTab={activeTab}
          onSelectTab={onSelectTab}
        />

        <main className="jt-page-body" data-width={width} id="jt-main-content" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}

/**
 * The sticky action area.
 *
 * It holds the primary action where the page header cannot keep it — below 768px the header's
 * action row is hidden and this is where the same action lives. `note` is the sentence that says
 * what is unsaved or what the action will do, because a bare Save button pinned to the bottom of a
 * long form does not say which of the four sections it is about to write.
 */
export function StickyActions({ note, children }: { note?: ReactNode; children: ReactNode }) {
  return (
    <div className="jt-sticky-actions">
      {note && <div className="jt-sticky-actions-note">{note}</div>}
      {children}
    </div>
  );
}

export { Sidebar } from './Sidebar';
export { TopBar } from './TopBar';
export { PageHeader } from './PageHeader';
